import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations } from "./translations";
import { appStrings } from "./appStrings";
import { statusLabel } from "./appStrings";

function mergeDeep(base, extra) {
  if (!extra || typeof extra !== "object") return base;
  const out = { ...base };
  for (const key of Object.keys(extra)) {
    const ev = extra[key];
    const bv = base[key];
    if (ev && typeof ev === "object" && !Array.isArray(ev)) {
      out[key] = mergeDeep(bv && typeof bv === "object" && !Array.isArray(bv) ? bv : {}, ev);
    } else {
      out[key] = ev;
    }
  }
  return out;
}

const LanguageContext = createContext(null);

export const SUPPORTED_LANGS = [
  { code: "en", label: "English", native: "English", short: "EN" },
  { code: "hi", label: "Hindi", native: "हिन्दी", short: "हिं" },
  { code: "bn", label: "Bengali", native: "বাংলা", short: "বা" },
  { code: "mr", label: "Marathi", native: "मराठी", short: "म" },
  { code: "te", label: "Telugu", native: "తెలుగు", short: "తె" },
  { code: "ta", label: "Tamil", native: "தமிழ்", short: "த" },
  { code: "kn", label: "Kannada", native: "ಕನ್ನಡ", short: "ಕ" },
  { code: "ml", label: "Malayalam", native: "മലയാളം", short: "മ" },
  { code: "pa", label: "Punjabi", native: "ਪੰਜਾਬੀ", short: "ਪੰ" },
];

const isSupported = (c) => SUPPORTED_LANGS.some((l) => l.code === c);

const detectDefault = () => {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem("chintamat:lang");
  if (isSupported(stored)) return stored;
  const browser = (navigator.language || "en").toLowerCase();
  if (browser.startsWith("hi")) return "hi";
  if (browser.startsWith("bn")) return "bn";
  if (browser.startsWith("mr")) return "mr";
  if (browser.startsWith("te")) return "te";
  if (browser.startsWith("ta")) return "ta";
  if (browser.startsWith("kn")) return "kn";
  if (browser.startsWith("ml")) return "ml";
  if (browser === "pa" || browser.startsWith("pa-")) return "pa";
  return "en";
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState("en");

  useEffect(() => {
    setLangState(detectDefault());
  }, []);

  const setLang = useCallback((next) => {
    if (!isSupported(next)) return;
    document.documentElement.classList.add("lang-switching");
    setLangState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("chintamat:lang", next);
      document.documentElement.setAttribute("lang", next);
    }
    setTimeout(() => document.documentElement.classList.remove("lang-switching"), 60);
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const t = useMemo(
    () => mergeDeep(translations[lang] || translations.en, appStrings[lang]),
    [lang],
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t,
      statusLabel: (code) => statusLabel(lang, code),
    }),
    [lang, setLang, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useT must be used within LanguageProvider");
  return ctx;
};
